import { markPaidFeature } from "./paidPreview";
import {
  getShowSecondaryUnits,
  getUnits,
  hidePreviewFeaturesPref,
  leftHandedPref,
  localeDefaultUnits,
  onUnitsChange,
  setShowSecondaryUnits,
  setUnits,
  showSnapCandidatesPref,
  themeProfilePref,
  type Units,
} from "../preferences";
import {
  PLAIN_PROFILE_LABEL,
  THEME_PROFILES,
  themeProfileById,
  type ThemeProfileId,
} from "../themeProfiles";
import { isFeatureEnabled, onFeatureFlagsChange } from "../featureFlags";

/**
 * UI settings dialog. Exposes the "Show secondary units" toggle and the
 * other UI-only preferences, so the topbar gear is the one place users look
 * for non-project knobs. Feature flags are managed in the StartupAPI admin
 * dashboard (#1244), not here.
 */
export class SettingsDialog {
  private host: HTMLElement;
  private offFlags: (() => void) | null = null;
  private offUnits: (() => void) | null = null;
  /** Told when the theme profile changes so the app can write the attribute
   *  and repaint the canvas — the dialog owns the control, not the theme. */
  private onThemeProfileChange:
    | ((id: ThemeProfileId | null) => void)
    | undefined;
  /** The Theme row (#190), kept out of the DOM while its flag is off. */
  private themeRow: HTMLElement | null = null;
  /** What the Theme row sits after, so it returns to the same place. */
  private themeRowAnchor: HTMLElement | null = null;
  /** Same insert / remove treatment as the Theme row, for the same reason:
   *  its gate can change while the dialog is open (#1204). */
  private leftHandedRow: HTMLElement | null = null;
  private leftHandedRowAnchor: HTMLElement | null = null;

  constructor(
    host: HTMLElement,
    opts: { onThemeProfileChange?: (id: ThemeProfileId | null) => void } = {},
  ) {
    this.host = host;
    this.onThemeProfileChange = opts.onThemeProfileChange;
  }

  open() {
    this.host.classList.add("open");
    this.host.innerHTML = "";

    const modal = document.createElement("div");
    modal.className = "modal settings-modal";

    const header = document.createElement("header");
    header.textContent = "Settings";
    modal.appendChild(header);

    const body = document.createElement("div");
    body.className = "body";

    const unitsRow = this.buildUnitsRow();
    body.appendChild(unitsRow);
    // Gated (#190): a Maker-and-up feature whose entitlement plumbing doesn't
    // exist yet, so the flag stands in — on by default, and the row disappears
    // rather than sitting there inert if it is turned off.
    //
    // Built once and inserted/removed by `syncThemeRow`, because the flag that
    // gates it is toggled from THIS dialog: evaluating the gate only while
    // building the body left the row on screen (and working) until the dialog
    // was closed and reopened.
    this.themeRow = this.buildThemeProfileRow();
    this.themeRowAnchor = unitsRow;
    this.syncThemeRow();

    const row = document.createElement("label");
    row.className = "settings-row";

    const checkbox = document.createElement("input");
    checkbox.type = "checkbox";
    checkbox.checked = getShowSecondaryUnits();
    checkbox.dataset.role = "settings-show-secondary-units";
    row.appendChild(checkbox);

    const text = document.createElement("span");
    text.className = "settings-row-text";
    const title = document.createElement("span");
    title.className = "settings-row-title";
    title.textContent = "Show secondary units";
    const desc = document.createElement("span");
    desc.className = "settings-row-desc";
    desc.textContent =
      "Display both millimeter and inch inputs side-by-side. " +
      "When off, only the primary unit's input is shown.";
    text.appendChild(title);
    text.appendChild(desc);
    row.appendChild(text);

    body.appendChild(row);

    // Snap-candidate dots (#367). Snap targets are invisible until one fires,
    // which makes them hard to find and hard to connect to the toggle that
    // catches them; this marks every one up front.
    const snapRow = document.createElement("label");
    snapRow.className = "settings-row";

    const snapCheckbox = document.createElement("input");
    snapCheckbox.type = "checkbox";
    snapCheckbox.checked = showSnapCandidatesPref.get();
    snapCheckbox.dataset.role = "settings-show-snap-candidates";
    snapRow.appendChild(snapCheckbox);

    const snapText = document.createElement("span");
    snapText.className = "settings-row-text";
    const snapTitle = document.createElement("span");
    snapTitle.className = "settings-row-title";
    snapTitle.textContent = "Show snap points";
    const snapDesc = document.createElement("span");
    snapDesc.className = "settings-row-desc";
    snapDesc.textContent =
      "Mark every point the cursor can snap to with a faint dot, so you can " +
      "see the targets before you drag. Shown only while point snapping is on.";
    snapText.appendChild(snapTitle);
    snapText.appendChild(snapDesc);
    snapRow.appendChild(snapText);

    body.appendChild(snapRow);

    this.leftHandedRow = this.buildLeftHandedRow();
    this.leftHandedRowAnchor = snapRow;
    this.syncLeftHandedRow();

    body.appendChild(this.buildHidePreviewRow());

    // Rows gated by a flag follow it live, for every user: the preview opt-out
    // above changes those gates from inside this dialog (#1204).
    this.offFlags = onFeatureFlagsChange(() => {
      this.syncThemeRow();
      this.syncLeftHandedRow();
    });

    modal.appendChild(body);

    const footer = document.createElement("footer");
    const closeBtn = document.createElement("button");
    closeBtn.className = "btn";
    closeBtn.textContent = "Close";
    closeBtn.dataset.act = "close";
    footer.appendChild(closeBtn);
    modal.appendChild(footer);

    checkbox.addEventListener("change", () => {
      setShowSecondaryUnits(checkbox.checked);
    });

    snapCheckbox.addEventListener("change", () => {
      showSnapCandidatesPref.set(snapCheckbox.checked);
    });

    modal.addEventListener("click", (ev) => {
      const btn = (ev.target as HTMLElement).closest(
        "[data-act]",
      ) as HTMLButtonElement | null;
      if (btn?.dataset.act === "close") this.close();
    });

    this.host.appendChild(modal);
    this.host.addEventListener("click", this.handleBackdrop);
    document.addEventListener("keydown", this.handleEscape);
  }

  close() {
    this.host.classList.remove("open");
    this.host.innerHTML = "";
    this.host.removeEventListener("click", this.handleBackdrop);
    document.removeEventListener("keydown", this.handleEscape);
    this.offFlags?.();
    this.offFlags = null;
    this.offUnits?.();
    this.offUnits = null;
  }

  /**
   * Which units lengths are shown in (#177).
   *
   * The SAME preference the toolbar's mm / in button toggles, not a separate
   * "default" the button then overrides — two stored values for one question
   * is how a settings dialog ends up disagreeing with the screen behind it.
   * Until either has been touched, the value comes from the browser's region
   * (see `unitsForLocales`), and the row says so, because otherwise a US
   * visitor opening Settings for the first time reads "Inches" as a choice
   * someone made rather than as a guess they are welcome to change.
   */
  private buildUnitsRow(): HTMLElement {
    const row = document.createElement("div");
    row.className = "settings-row settings-row--control";

    const text = document.createElement("span");
    text.className = "settings-row-text";
    // A real <label for> rather than an aria-label on the select: the visible
    // word "Units" IS the control's name, so it should be the accessible name
    // too, and clicking it should reach the control.
    const title = document.createElement("label");
    title.className = "settings-row-title";
    title.textContent = "Units";
    title.htmlFor = "settings-units";
    const desc = document.createElement("span");
    desc.className = "settings-row-desc";
    text.appendChild(title);
    text.appendChild(desc);
    row.appendChild(text);

    const select = document.createElement("select");
    select.id = "settings-units";
    select.dataset.role = "settings-units";
    for (const [value, label] of [
      ["mm", "Metric (mm)"],
      ["in", "Imperial (in)"],
    ] as const) {
      const opt = document.createElement("option");
      opt.value = value;
      opt.textContent = label;
      select.appendChild(opt);
    }
    row.appendChild(select);

    const sync = (u: Units) => {
      select.value = u;
      desc.textContent =
        u === localeDefaultUnits()
          ? "Lengths are shown and typed in this system. Chosen for your region — change it here or with the mm / in button in the top bar."
          : "Lengths are shown and typed in this system. Same setting as the mm / in button in the top bar.";
    };
    sync(getUnits());

    select.addEventListener("change", () => {
      setUnits(select.value === "in" ? "in" : "mm");
    });
    // The toolbar button writes the same preference, so follow it rather than
    // going stale behind the dialog.
    this.offUnits = onUnitsChange(sync);

    return row;
  }

  /** Put the Left-handed row in the DOM, or take it out, to match its gate. */
  private syncLeftHandedRow(): void {
    const row = this.leftHandedRow;
    const anchor = this.leftHandedRowAnchor;
    if (!row || !anchor) return;
    const wanted = isFeatureEnabled("left-handed-layout");
    const present = row.parentElement !== null;
    if (wanted && !present) anchor.after(row);
    else if (!wanted && present) row.remove();
  }

  /**
   * "Don't show me preview features" (#1204): the explicit opt-out from the
   * early-access features marked with the Preview badge. Unchecked by
   * default, so everyone gets them during the public preview.
   */
  private buildHidePreviewRow(): HTMLElement {
    const row = document.createElement("label");
    row.className = "settings-row";

    const checkbox = document.createElement("input");
    checkbox.type = "checkbox";
    checkbox.checked = hidePreviewFeaturesPref.get();
    checkbox.dataset.role = "settings-hide-preview-features";
    row.appendChild(checkbox);

    const text = document.createElement("span");
    text.className = "settings-row-text";
    const title = document.createElement("span");
    title.className = "settings-row-title";
    title.textContent = "Don't show me preview features";
    const desc = document.createElement("span");
    desc.className = "settings-row-desc";
    desc.append("Hide the early-access features marked with ");
    // A sample of the badge itself, so the description shows what it means.
    const sample = document.createElement("span");
    sample.setAttribute("data-preview", "");
    sample.setAttribute("role", "img");
    sample.setAttribute("aria-label", "the Preview badge");
    desc.append(
      sample,
      ". During the public preview they're available to everyone.",
    );
    text.appendChild(title);
    text.appendChild(desc);
    row.appendChild(text);

    checkbox.addEventListener("change", () => {
      hidePreviewFeaturesPref.set(checkbox.checked);
    });
    return row;
  }

  /** Put the Theme row in the DOM, or take it out, to match the flag. Removal
   *  rather than `hidden`: `.settings-row--control` sets its own `display`,
   *  which outranks the UA sheet's `[hidden] { display: none }` and would leave
   *  a "hidden" row perfectly visible. */
  private syncThemeRow(): void {
    const row = this.themeRow;
    const anchor = this.themeRowAnchor;
    if (!row || !anchor) return;
    const wanted = isFeatureEnabled("theme-profiles");
    const present = row.parentElement !== null;
    if (wanted && !present) anchor.after(row);
    else if (!wanted && present) row.remove();
  }

  /**
   * Theme profile (#190) — which palette the interface wears.
   *
   * Deliberately separate from the light/dark toggle in the top bar rather
   * than folded into one list: each profile carries its own light AND dark
   * chrome, so the two questions ("which palette" and "light or dark") are
   * independent and a combined list would multiply out.
   *
   * The description names what a profile does and does not reach, because the
   * obvious guess — that a leather-themed palette recolors the leather — is
   * wrong, and that is the future material feature rather than this one.
   */
  private buildThemeProfileRow(): HTMLElement {
    const row = document.createElement("div");
    row.className = "settings-row settings-row--control";

    const text = document.createElement("span");
    text.className = "settings-row-text";
    const title = document.createElement("label");
    title.className = "settings-row-title";
    title.textContent = "Theme";
    title.htmlFor = "settings-theme-profile";
    markPaidFeature(title, "interface-theme-colors");
    const desc = document.createElement("span");
    desc.className = "settings-row-desc";
    text.appendChild(title);
    text.appendChild(desc);
    row.appendChild(text);

    const select = document.createElement("select");
    select.id = "settings-theme-profile";
    select.dataset.role = "settings-theme-profile";

    // Plain is the absence of a profile, so its option carries the empty
    // string and `set(null)` clears the preference.
    const plain = document.createElement("option");
    plain.value = "";
    plain.textContent = PLAIN_PROFILE_LABEL;
    select.appendChild(plain);
    for (const profile of THEME_PROFILES) {
      const opt = document.createElement("option");
      opt.value = profile.id;
      opt.textContent = profile.label;
      select.appendChild(opt);
    }
    row.appendChild(select);

    const sync = (id: ThemeProfileId | null) => {
      select.value = id ?? "";
      const profile = themeProfileById(id);
      desc.textContent = profile
        ? `${profile.hint} Colors the interface, guides and previews — the leather itself stays the same.`
        : "Colors the interface, guides and previews. The leather, the cutting mat and the export layers stay the same in every theme.";
    };
    sync(themeProfilePref.get());

    select.addEventListener("change", () => {
      const next = (select.value || null) as ThemeProfileId | null;
      sync(next);
      this.onThemeProfileChange?.(next);
    });

    return row;
  }

  /**
   * Mirror the workspace for a left-handed maker (#332).
   *
   * Worded for the hand rather than for handedness: the reason to want it is
   * that a drawing hand lies across whichever edge it reaches over, and on a
   * tablet that hides the toolbar it is reaching for.
   *
   * Built only when `left-handed-layout` is on — a row whose setting the app
   * would then ignore is worse than no row at all.
   */
  private buildLeftHandedRow(): HTMLElement {
    const row = document.createElement("label");
    row.className = "settings-row";

    const checkbox = document.createElement("input");
    checkbox.type = "checkbox";
    checkbox.checked = leftHandedPref.get();
    checkbox.dataset.role = "settings-left-handed";
    row.appendChild(checkbox);

    const text = document.createElement("span");
    text.className = "settings-row-text";
    const title = document.createElement("span");
    title.className = "settings-row-title";
    title.textContent = "Left-handed layout";
    markPaidFeature(title, "configurable-layout");
    const desc = document.createElement("span");
    desc.className = "settings-row-desc";
    desc.textContent =
      "Put the tools down the right edge and the panels down the left, so a " +
      "left hand resting on the screen isn't covering the toolbar it's " +
      "reaching for.";
    text.appendChild(title);
    text.appendChild(desc);
    row.appendChild(text);

    checkbox.addEventListener("change", () => {
      leftHandedPref.set(checkbox.checked);
    });
    return row;
  }

  private handleBackdrop = (ev: Event) => {
    if (ev.target === this.host) this.close();
  };

  private handleEscape = (ev: KeyboardEvent) => {
    if (ev.key === "Escape") this.close();
  };
}
